// Pagination d'un index (les deux chevrons « page précédente / suivante »).
//
// Les flèches sont de simples liens, sans `data-turbo-frame` dans le cas d'un index Rails
// Admin : Turbo recharge donc la frame qui les contient — sur un index en tableau, la frame
// de l'index entier, puisque `…_search_results` y est un <div> (une turbo-frame autour d'un
// <table> est déplacée par le parseur HTML). C'est exactement la région que remplacent déjà
// les onglets de scope.
//
// Jusqu'ici, entre le clic et l'arrivée de la page suivante, rien ne bougeait : même
// en-tête, mêmes lignes, même plage de résultats affichée. C'est le cas où l'attente est la
// plus trompeuse de tout l'index — les autres commandes (recherche, filtres d'en-tête,
// scopes) accusent toutes réception, la pagination était la dernière à rester muette. Et
// comme l'écran ne change pas, l'utilisateur reclique : la seconde visite annule la
// première, et il atterrit deux pages plus loin ou nulle part.
//
// Même remède que partout ailleurs, plus le pendant local : le corps du tableau passe en
// barres (`skeletonizeSearchResults`), et la barre de pagination elle-même — là où est le
// curseur — se met en attente (`aria-busy`, cf. `_pagination.scss` : plage estompée, flèches
// qui ne répondent plus).
//
// Rien à défaire : la région squelettisée et la barre de pagination font toutes deux partie
// de ce que Turbo remplace, y compris en cas d'échec (`showTurboFrameLoadError`, cf.
// entrypoints/common).

import { clearSearchResultsSkeleton, skeletonizeSearchResults } from '../entrypoints/common';

// Les flèches désactivées sont des <span>, pas des <a> : elles ne peuvent pas matcher.
const PAGINATION_LINK_SELECTOR = 'a.pagy-nav__link';

// Même résolution que FrameController#findFrameElement : la frame désignée par
// `data-turbo-frame` si c'en est une (pagy_nav accepte un `turbo_frame:`, dont se servent le
// grand livre et les TraceTable), sinon la frame qui contient le lien.
const regionForPaginationLink = (link: HTMLElement): HTMLElement | null => {
  const frameId = link.dataset.turboFrame;
  const targeted = frameId ? document.getElementById(frameId) : null;
  if (targeted?.tagName === 'TURBO-FRAME') return targeted;

  return link.closest('turbo-frame');
};

export function initializeIndexPaginationSkeleton() {
  if ((window as any).__indexPaginationSkeletonInitialized) return;
  (window as any).__indexPaginationSkeletonInitialized = true;

  // `turbo:click` et non un écouteur de clic : Turbo l'émet pour tous les liens qu'il prend
  // en charge, frames comprises — c'est l'intercepteur de la frame qui l'annule ensuite pour
  // piloter la navigation lui-même, d'où l'absence de garde sur `defaultPrevented`.
  document.addEventListener('turbo:click', (event) => {
    const link = (event.target as Element | null)?.closest<HTMLElement>(PAGINATION_LINK_SELECTOR);
    if (!link) return;

    link.closest('.pagy-nav')?.setAttribute('aria-busy', 'true');

    // Un index en cartes ou en lignes n'a pas de tableau à squelettiser : `skeletonizeSearchResults`
    // ne trouve pas de <tbody> et ne fait rien. Leurs aperçus portent `turbo-blur`, que le
    // `[busy]` posé par Turbo sur la frame floute déjà (cf. config/utils) — et la barre de
    // pagination, elle, s'est mise en attente dans tous les cas.
    const region = regionForPaginationLink(link);
    if (region) skeletonizeSearchResults(region);
  });

  // La frame survit au rendu de son contenu : sans ça elle resterait annoncée occupée. Appel
  // idempotent — `index_scope_skeleton.ts` fait le même nettoyage sur le même événement, et
  // ne fait que supprimer un attribut déjà absent.
  document.addEventListener('turbo:frame-load', (event) => {
    clearSearchResultsSkeleton(event.target as Element | null);
  });
}
